
import React from 'react';
import { Link } from 'react-router-dom';
import { Star, MapPin, Calendar, Clock, Globe, MessageCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';

export interface TherapistProps {
  id: string;
  name: string;
  title?: string;
  specialties: string[];
  rating: number;
  reviews?: number;
  reviewCount?: number;
  location: string;
  languages?: string[];
  price: number;
  currency: string;
  image?: string;
  verified?: boolean;
  onlineAvailable?: boolean;
  isOnline?: boolean;
  available?: boolean;
  nextAvailable?: string;
  sessionCount?: number;
  summary?: string;
  activationStatus?: string;
}

interface TherapistCardProps {
  therapist: TherapistProps;
  showBookingButton?: boolean;
  compact?: boolean;
}

const TherapistCard: React.FC<TherapistCardProps> = ({ 
  therapist, 
  showBookingButton = true,
  compact = false 
}) => {
  const getInitials = (name: string) => {
    return name.split(' ').map(n => n[0]).join('').toUpperCase();
  };

  const reviewCount = therapist.reviews || therapist.reviewCount || 0;

  return (
    <Card className="h-full transition-all duration-200 hover:shadow-lg border border-gray-200 hover:border-blue-300">
      <CardHeader className={compact ? "p-4 pb-2" : "p-6 pb-4"}>
        <div className="flex items-start gap-4">
          <Avatar className="h-16 w-16 border-2 border-blue-100">
            <AvatarImage src={therapist.image} alt={therapist.name} />
            <AvatarFallback className="bg-blue-100 text-blue-700 font-semibold">
              {getInitials(therapist.name)}
            </AvatarFallback>
          </Avatar>
          
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1 flex-wrap">
              <h3 className="font-semibold text-lg text-gray-900 truncate">
                {therapist.name}
              </h3>
              {therapist.verified && (
                <Badge variant="secondary" className="text-xs bg-green-100 text-green-700">
                  Verified
                </Badge>
              )}
              {therapist.activationStatus === 'pending_activation' && (
                <Badge variant="outline" className="text-xs bg-yellow-50 text-yellow-700 border-yellow-300">
                  Pendiente de Activación
                </Badge>
              )}
            </div>
            
            {therapist.title && (
              <p className="text-sm text-gray-600 mb-2">{therapist.title}</p>
            )}
            
            <div className="flex items-center gap-2 mb-2">
              <div className="flex items-center gap-1">
                <Star className="h-4 w-4 fill-yellow-400 text-yellow-400" />
                <span className="text-sm font-medium">{therapist.rating}</span>
                <span className="text-sm text-gray-500">({reviewCount} reviews)</span>
              </div>
            </div>

            <div className="flex items-center gap-1 text-sm text-gray-600 mb-1">
              <MapPin className="h-3.5 w-3.5" />
              <span className="truncate">{therapist.location}</span>
            </div>

            {(therapist.onlineAvailable || therapist.isOnline) && (
              <div className="flex items-center gap-1 text-sm text-green-600">
                <Globe className="h-3.5 w-3.5" />
                <span>Online sessions available</span>
              </div>
            )}
          </div>
        </div>
      </CardHeader>

      <CardContent className={compact ? "p-4 pt-0" : "p-6 pt-0"}>
        <div className="space-y-3">
          <div>
            <p className="text-xs font-medium text-gray-500 mb-1">SPECIALTIES</p>
            <div className="flex flex-wrap gap-1">
              {therapist.specialties.slice(0, 3).map((specialty, index) => (
                <Badge key={index} variant="outline" className="text-xs">
                  {specialty}
                </Badge>
              ))}
              {therapist.specialties.length > 3 && (
                <Badge variant="outline" className="text-xs">
                  +{therapist.specialties.length - 3} more
                </Badge>
              )}
            </div>
          </div>

          {therapist.languages && therapist.languages.length > 0 && (
            <div>
              <p className="text-xs font-medium text-gray-500 mb-1">LANGUAGES</p>
              <div className="flex flex-wrap gap-1">
                {therapist.languages.slice(0, 3).map((language, index) => (
                  <Badge key={index} variant="secondary" className="text-xs">
                    {language}
                  </Badge>
                ))}
                {therapist.languages.length > 3 && (
                  <Badge variant="secondary" className="text-xs">
                    +{therapist.languages.length - 3}
                  </Badge>
                )}
              </div>
            </div>
          )}

          <div className="flex items-center justify-between pt-2">
            <div>
              <p className="text-lg font-semibold text-gray-900">
                {therapist.currency}{therapist.price}
                <span className="text-sm font-normal text-gray-500">/session</span>
              </p>
              {therapist.nextAvailable && (
                <div className="flex items-center gap-1 text-xs text-gray-500 mt-1">
                  <Calendar className="h-3 w-3" />
                  <span>Next: {therapist.nextAvailable}</span>
                </div>
              )}
            </div>
            
            {showBookingButton && (
              <div className="flex gap-2">
                <Button variant="outline" size="sm" asChild>
                  <Link to={`/therapist/${therapist.id}`}>
                    View Profile
                  </Link>
                </Button>
                <Button size="sm" className="bg-blue-600 hover:bg-blue-700" asChild>
                  <Link to={`/book/${therapist.id}`}>
                    <MessageCircle className="h-3.5 w-3.5 mr-1" />
                    Book
                  </Link>
                </Button>
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default TherapistCard;
